/**
 * Line-level errors, said in English.
 *
 * When a custom line fails schema validation the backend hands back the raw
 * Marshmallow payload stringified into `line_item_results[i].error`:
 *
 *   Validation failed: {'assembly_components': {0: {'name': ['Missing data …']}}}
 *
 * An estimator cannot act on that. This module maps the payload to a sentence
 * that names the thing to fix, and hands the raw text back separately so it can
 * live behind a "technical details" disclosure instead of on the screen.
 *
 * RULE: never render a Python/JSON dict to the user.
 */

const VALIDATION_PREFIX = /^validation failed:\s*/i;

export const GENERIC_LINE_ERROR =
  "This line needs attention before it can price.";

/**
 * Field name → what the estimator has to do about it. Order matters: the first
 * matching entry wins the headline when several fields failed, so the most
 * actionable instruction is the one that gets read.
 */
const FIELD_MESSAGES = [
  ["assembly_components", "Add at least one component to this custom line."],
  ["name", "Give each component a name."],
  ["unit_cost", "Enter a cost for each component."],
  ["unit", "Choose a unit for each material component."],
  ["quantity_per_unit", "Enter how many of each component the line uses."],
  ["hours_per_unit", "Enter the hours for each operation component."],
  ["department", "Choose a department for each operation component."],
  ["rip_cut", "Rip-cut details only apply to components measured in sheets."],
  ["material_id", "Pick a material for each material component."],
  ["quantity", "Enter a quantity greater than zero for this line."],
  ["mode", "This line is in an unrecognised mode — switch it back to Product."],
];

function toRawText(error) {
  if (error === null || error === undefined || error === "") return "";
  if (typeof error === "string") return error;
  try {
    return JSON.stringify(error);
  } catch (serialisationError) {
    return String(error);
  }
}

/**
 * @param error the backend's `error` value (string, or defensively an object)
 * @returns {{ message: string, detail: string|null }}
 *   `message` is always safe to render. `detail` is the raw payload, present
 *   only when it is machine-shaped and therefore worth hiding behind a
 *   disclosure rather than showing.
 */
export function describeLineError(error) {
  const raw = toRawText(error);
  if (!raw) return { message: "", detail: null };

  const isValidationPayload = VALIDATION_PREFIX.test(raw);
  const looksLikeDict = /[{[]/.test(raw);

  // A plain sentence from the backend ("Custom line item requires at least one
  // component.") is already the right thing to show.
  if (!isValidationPayload && !looksLikeDict) {
    return { message: raw, detail: null };
  }

  const payload = raw.replace(VALIDATION_PREFIX, "");
  // Quoted keys are the field names; the values are Marshmallow's own English.
  const fields = new Set(
    Array.from(payload.matchAll(/['"]([A-Za-z_][A-Za-z0-9_]*)['"]\s*:/g)).map(
      (match) => match[1],
    ),
  );

  if (fields.has("assembly_components")) {
    // The payload's own shape tells the two failures apart: a component that
    // exists but is incomplete carries an inner field key ({'0': {'name': …}}),
    // while a genuinely empty list has no keys inside assembly_components at
    // all. Message text ("Shorter than minimum length…") is the same for both,
    // so never classify on it.
    const inner = FIELD_MESSAGES.find(
      ([field]) => field !== "assembly_components" && fields.has(field),
    );
    if (inner) return { message: inner[1], detail: raw };
  }

  const match = FIELD_MESSAGES.find(([field]) => fields.has(field));
  return { message: match ? match[1] : GENERIC_LINE_ERROR, detail: raw };
}

/** Convenience for places that can only show one string (tooltips, aria). */
export function lineErrorText(error) {
  return describeLineError(error).message;
}

/**
 * The backend's `material_error` payload, said in English.
 *
 * Unlike a validation `error`, a material failure is not a line that "did not
 * price" — labor still charged, and the line still counts in the job total.
 * The two messages below say what happened and what fixes it; neither one
 * ever says "did not price".
 *
 * @param materialError `{code, message, material_class, candidates}` from the
 *   backend, or null/undefined when the line has no material failure.
 * @returns {{message: string, candidates: Array}}
 */
export function describeMaterialError(materialError) {
  if (!materialError) return { message: "", candidates: [] };
  const candidates = Array.isArray(materialError.candidates)
    ? materialError.candidates
    : [];
  const message =
    materialError.code === "ambiguous_material"
      ? "More than one material fits this class at different prices, so no material was charged. Pick a specific sheet, or enter a Quoted price."
      : "No material record matches this line's class — shop labor was charged, material was not. Pick a specific sheet, or enter a Quoted price.";
  return { message, candidates };
}

export default describeLineError;
